'use client';

import { useGSAP } from '@gsap/react';
import Image from 'next/image';
import { FC, useRef } from 'react';

import AnimatedLink from '@/components/shared/AnimatedLink';
import BookACallButton from '@/components/shared/Footer/BookACallButton';
import { FooterDto } from '@/components/shared/Footer/types';
import VideoBackgroundFooter from '@/components/shared/Footer/VideoBackground';
import { Typography } from '@/components/shared/Typography';
import StrapiRichContent from '@/components/StrapiRichContent';
import { isArrayNotEmpty } from '@/helpers/array';
import useDevice from '@/hooks/useDevice';
import { useModal } from '@/hooks/useModal';
import { gsap } from '@/lib/gsap';

import css from './style.module.scss';
import { FooterReferralDto } from './types';

interface Props {
	data: FooterDto;
	dataRefferal: FooterReferralDto;
}

const FooterPartnership: FC<Props> = ({ data, dataRefferal }) => {
	const { socials } = data;
	const { title, subtitle, cta } = dataRefferal;
	const { openModal } = useModal();

	const device = useDevice();
	const wrapperRef = useRef<HTMLDivElement>(null);

	useGSAP(
		() => {
			const wrapper = wrapperRef.current;
			const title = wrapper?.querySelector(`.${css.title}`);
			const titleLines = title?.querySelectorAll('p');
			const subtitle = wrapper?.querySelector(`.${css.subtitle}`);
			const btn = wrapper?.querySelector(`.${css.callButton}`);

			if (!wrapper || !title || !titleLines?.length) return;

			const mm = gsap.matchMedia();

			mm.add(
				{
					isDesktop: '(min-width: 992px)',
					isMobile: '(max-width: 991px)'
				},
				context => {
					const { isMobile } = context.conditions as gsap.Conditions;

					if (isMobile) return;

					gsap.set(titleLines, {
						opacity: 0,
						y: 40,
						filter: 'blur(10px)'
					});

					gsap.to(titleLines, {
						opacity: 1,
						y: 0,
						filter: 'blur(0px)',
						duration: 1.1,
						ease: 'power3.out',
						stagger: 0.15,
						scrollTrigger: {
							trigger: title,
							start: 'top 80%',
							toggleActions: 'play none none reverse'
						}
					});

					gsap.to(title, {
						yPercent: -40,
						ease: 'none',
						scrollTrigger: {
							trigger: wrapper,
							start: 'top bottom',
							end: 'bottom top',
							scrub: true
						}
					});

					if (subtitle) {
						gsap.set(subtitle, {
							opacity: 0,
							y: 40,
							filter: 'blur(10px)'
						});

						gsap.to(subtitle, {
							opacity: 1,
							y: 0,
							filter: 'blur(0px)',
							duration: 1,
							ease: 'power3.out',
							scrollTrigger: {
								trigger: subtitle,
								start: 'top 80%',
								toggleActions: 'play none none reverse'
							}
						});
					}

					if (btn) {
						gsap.set(btn, {
							opacity: 0,
							y: 40,
							filter: 'blur(10px)'
						});

						gsap.to(btn, {
							opacity: 1,
							y: 0,
							filter: 'blur(0px)',
							duration: 0.6,
							ease: 'power2.out',
							scrollTrigger: {
								trigger: btn,
								start: 'top 80%',
								toggleActions: 'play none none reverse'
							}
						});
					}
				}
			);

			return () => mm.revert();
		},
		{ scope: wrapperRef }
	);

	return (
		<footer ref={wrapperRef} id="footer" className={css.wrapper}>
			<VideoBackgroundFooter>
				<div className={css.content}>
					<Typography uppercase className={css.title} variant="h1" tag="div">
						{title ? <StrapiRichContent content={title} /> : null}
					</Typography>
					{subtitle ? (
						<Typography className={css.subtitle} variant="body-l" tag="div">
							<StrapiRichContent content={subtitle} />
						</Typography>
					) : null}
					{cta ? (
						<BookACallButton
							className={css.callButton}
							preview={cta.image}
							onClick={() => openModal('CALENDAR_BOOKING')}
						>
							{cta?.text}
						</BookACallButton>
					) : null}
				</div>

				<div className={css.bottomWrapper}>
					<ul className={css.mediaList}>
						{isArrayNotEmpty(socials) &&
							socials.map(({ id, name, url }, index) => (
								<li key={`${id}-${index}`}>
									{device === 'desktop' ? (
										<AnimatedLink href={url} target="_blank" rel="noreferrer">
											{name}
										</AnimatedLink>
									) : (
										<a href={url} target="_blank" rel="noreferrer">
											<Image
												src={`/assets/icons/socials/${name}.svg`}
												width="28"
												height="28"
												alt={name}
											/>
										</a>
									)}
								</li>
							))}
					</ul>

					{device === 'desktop' && <span className={css.copy}>{new Date().getFullYear()}</span>}
				</div>
			</VideoBackgroundFooter>
		</footer>
	);
};

export default FooterPartnership;
